import { FloodGame } from './FloodGame.js';
import { FloodRenderer } from './FloodRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Edge, isRimEdge } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, FinePoint } from '../../input/KeyboardController.js';
import { fineToEdge } from '../../input/fineGeometry.js';

export class FloodInput extends BaseInput<FloodGame, FloodRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: FloodGame,
        renderer: FloodRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const edge = this.detector.detectEdge(x, y, 0.2, true);
        if (edge && !isRimEdge(edge) && this.game.hasWall(edge)) {
            this.game.toggleWall(edge);
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        if (this.gateMode && isVortexGame(this.game)) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.toggleVortexGate(cell)) {
                this.onUpdate();
            }
            return;
        }

        // Detect edges including rim edges
        const edge = this.detector.detectEdge(x, y, 0.2, true);
        if (edge) {
            // Top rim edge -> faucet placement
            if (edge.orient === 'h' && edge.pa.row === 0) {
                this.game.setFaucet(edge.pa.col);
                this.onUpdate();
                return;
            }

            // Internal edge -> wall placement
            if (!isRimEdge(edge)) {
                if (this.game.toggleWall(edge)) {
                    this.onUpdate();
                }
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Walls sit on edges, so the cursor rides the half-grid like Walls — a tap on an
    // edge toggles one wall. But walls run *along* the grid lines, so the trace
    // anchors on intersections and lays a wall per hop. The rim is in range: its top
    // row is the faucet.

    usesFineCursor(): boolean {
        return true;
    }

    /** The rim is reachable: walls must meet the boundary, and the faucet sits on it. */
    allowsFineRim(): boolean {
        return true;
    }

    /** Water falls out of the grid rather than round to the other side. */
    wrapsFineCursor(): boolean {
        return false;
    }

    /** Walls run along the grid lines, so the trace hops intersection to intersection. */
    tracesFineIntersections(): boolean {
        return true;
    }

    /** The top rim is the faucet row — slide along it slot by slot. */
    fineTopRimSlots(): boolean {
        return true;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        // A tap on the top rim moves the faucet to that slot.
        if (p.r === 0 && p.c % 2 === 1) {
            this.game.setFaucet((p.c - 1) / 2);
            return true;
        }
        const edge = this.fineToEdge(p);
        if (!edge || isRimEdge(edge)) return false;
        return this.game.toggleWall(edge);
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        const edge = this.fineToEdge(p);
        if (!edge || isRimEdge(edge) || !this.game.hasWall(edge)) return false;
        return this.game.toggleWall(edge);
    }

    /** Each trace hop lays (or lifts) the wall on the line just crossed. */
    traceFineEdge(edge: FinePoint): boolean {
        if (this.gateMode) return false;
        const e = this.fineToEdge(edge);
        // The boundary is drawn as a wall but isn't one you can place.
        if (!e || isRimEdge(e)) return false;
        return this.game.toggleWall(e);
    }

    /** `X` goes to the faucet — where the water starts. */
    fineCursorOrigin(): FinePoint {
        return { r: 0, c: 2 * this.game.getFaucetCol() + 1 };
    }

    private fineToEdge(p: FinePoint): Edge | null {
        return fineToEdge(this.game.getGrid(), p);
    }
}
